import React, { useState, useCallback } from 'react';
import FactsBatchPanel from './FactsBatchPanel';
import PipelineBatchPanel from './PipelineBatchPanel';
import VoiceoverBatchPanel from './VoiceoverBatchPanel';
import AnswerBatchPanel from './AnswerBatchPanel';
import IllustrationsBatchPanel from './IllustrationsBatchPanel';
import HintsBatchPanel from './HintsBatchPanel';

// One shared plate for all the pipelines so they don't each eat a full box of
// vertical space. Tabs are in running order — facts first, then the text
// writing, then voiceover and answers. A tab bar switches which one is shown;
// the hidden panel stays MOUNTED (display:none) so its live SSE progress and
// state survive tab switches. Each tab shows a compact status badge and a
// pulsing dot while its pipeline is running.

const TABS = [
  { key: 'facts', label: '🔍 Facts', color: '#b06000' },
  { key: 'text', label: '⚡ Text', color: '#1a73e8' },
  { key: 'voiceover', label: '🔊 Voiceover', color: '#8e44ad' },
  { key: 'answers', label: '✅ Answers', color: '#0b8043' },
  { key: 'illustrations', label: '🖼 Illustrations', color: '#c2185b' },
  { key: 'hints', label: '💡 Hints', color: '#e67e00' },
];

// Small per-tab status: "running dot" + done/total when a batch exists.
function TabBadge({ st, color }) {
  if (!st || !st.hasBatch) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 6, fontSize: 11, color: '#5f6368' }}>
      {st.running && (
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, animation: 'batchTabPulse 1s ease-in-out infinite' }} />
      )}
      {st.done || 0}/{st.total || 0}
      {!st.running && st.status ? ` · ${st.status}` : ''}
    </span>
  );
}

// v2: default is now collapsed. Bumping the key resets the one-time saved
// preference so the new default actually takes effect for existing users.
const COLLAPSE_KEY = 'batchPlateCollapsed_v2';

const BatchPanels = (props) => {
  const [tab, setTab] = useState('facts');
  const [status, setStatus] = useState({ facts: null, text: null, voiceover: null, answers: null, illustrations: null, hints: null });
  // Collapse the whole tabbed plate to just its tab bar (badges stay visible).
  // The sub-panels stay MOUNTED (the bodies are only display:none'd) so live SSE
  // progress survives a collapse. Choice is remembered across reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      return saved === null ? true : saved === '1'; // default collapsed
    } catch (e) { return true; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch (e) { /* ignore */ }
    return next;
  });

  // Stable callbacks so the panels' status effect doesn't loop.
  const onFactsStatus = useCallback((s) => setStatus((p) => ({ ...p, facts: s })), []);
  const onTextStatus = useCallback((s) => setStatus((p) => ({ ...p, text: s })), []);
  const onVoiceStatus = useCallback((s) => setStatus((p) => ({ ...p, voiceover: s })), []);
  const onAnswerStatus = useCallback((s) => setStatus((p) => ({ ...p, answers: s })), []);
  const onIllusStatus = useCallback((s) => setStatus((p) => ({ ...p, illustrations: s })), []);
  const onHintsStatus = useCallback((s) => setStatus((p) => ({ ...p, hints: s })), []);

  const outer = {
    border: '1px solid var(--border,#ddd)', borderRadius: 8,
    margin: '8px 8px 0', background: 'rgba(0,0,0,0.015)', overflow: 'hidden',
    // The parent (.locations-list-container) is a flex column; without this a
    // flex item with overflow:hidden is allowed to shrink below its content
    // and the whole plate collapses to a ~1px sliver.
    flexShrink: 0,
  };
  const tabBar = { display: 'flex', gap: 2, borderBottom: '1px solid var(--border,#eee)', padding: '4px 4px 0' };

  const tabBtn = (t) => {
    const activeTab = tab === t.key;
    return {
      display: 'flex', alignItems: 'center', border: 'none', cursor: 'pointer',
      padding: '6px 12px', fontSize: 13, fontWeight: activeTab ? 700 : 500,
      color: activeTab ? t.color : '#5f6368',
      background: activeTab ? 'var(--bg,#fff)' : 'transparent',
      borderRadius: '6px 6px 0 0',
      borderBottom: activeTab ? `2px solid ${t.color}` : '2px solid transparent',
    };
  };

  // Clicking a tab always shows its body — so a click while collapsed expands
  // the plate (and switches to that tab) rather than silently doing nothing.
  const selectTab = (key) => {
    setTab(key);
    if (collapsed) { setCollapsed(false); try { localStorage.setItem(COLLAPSE_KEY, '0'); } catch (e) { /* ignore */ } }
  };

  return (
    <div style={outer}>
      {/* Keyframes for the running dot — scoped, injected once with the shell. */}
      <style>{'@keyframes batchTabPulse{0%,100%{opacity:1}50%{opacity:0.3}}'}</style>
      {/* The whole bar toggles collapse — except the tab buttons, which stop
          the click from bubbling so they only switch tabs. */}
      <div
        style={{ ...tabBar, cursor: 'pointer' }}
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {TABS.map((t) => (
          <button key={t.key} style={tabBtn(t)} onClick={(e) => { e.stopPropagation(); selectTab(t.key); }}>
            {t.label}
            <TabBadge st={status[t.key]} color={t.color} />
          </button>
        ))}
        {/* Collapse indicator only — the click is handled by the bar. */}
        <span
          aria-hidden="true"
          style={{ marginLeft: 'auto', alignSelf: 'center', color: '#5f6368', fontSize: 13, padding: '4px 8px' }}
        >{collapsed ? '▸' : '▾'}</span>
      </div>

      {/* Bodies stay mounted while collapsed (display:none) so live SSE state
          survives — same reason the inactive tab isn't unmounted. */}
      <div style={{ display: collapsed ? 'none' : 'block' }}>
        <div style={{ display: tab === 'facts' ? 'block' : 'none' }}>
          <FactsBatchPanel
            {...props}
            initialBatch={props.initialFactsBatch}
            embedded
            onStatus={onFactsStatus}
          />
        </div>
        <div style={{ display: tab === 'text' ? 'block' : 'none' }}>
          <PipelineBatchPanel
            {...props}
            initialBatch={props.initialBatch}
            embedded
            onStatus={onTextStatus}
          />
        </div>
        <div style={{ display: tab === 'voiceover' ? 'block' : 'none' }}>
          <VoiceoverBatchPanel
            {...props}
            initialBatch={props.initialVoiceoverBatch}
            embedded
            onStatus={onVoiceStatus}
          />
        </div>
        <div style={{ display: tab === 'answers' ? 'block' : 'none' }}>
          <AnswerBatchPanel
            {...props}
            initialBatch={props.initialAnswerBatch}
            embedded
            onStatus={onAnswerStatus}
          />
        </div>
        <div style={{ display: tab === 'illustrations' ? 'block' : 'none' }}>
          <IllustrationsBatchPanel
            {...props}
            embedded
            onStatus={onIllusStatus}
          />
        </div>
        <div style={{ display: tab === 'hints' ? 'block' : 'none' }}>
          <HintsBatchPanel
            {...props}
            initialBatch={props.initialHintsBatch}
            embedded
            onStatus={onHintsStatus}
          />
        </div>
      </div>
    </div>
  );
};

export default BatchPanels;
